function tableKey(table) {
    return `${table.schema}.${table.name}`;
}
function byKey(items, keyFn) {
    const map = new Map();
    for (const item of items)
        map.set(keyFn(item), item);
    return map;
}
function describeTable(table) {
    return `${tableKey(table)} (${table.columns.length} sloupců)`;
}
function describeColumn(column) {
    const type = column.canonicalType ?? column.rawType;
    const parts = [type, column.nullable ? 'NULL' : 'NOT NULL'];
    if (column.defaultValue)
        parts.push(`DEFAULT ${column.defaultValue}`);
    if (column.comment)
        parts.push(`-- ${column.comment}`);
    return parts.join(' ');
}
function describePrimaryKey(pk) {
    return pk.columns.join(', ');
}
function describeForeignKey(fk) {
    return `${fk.columns.join(', ')} → ${fk.referencedSchema}.${fk.referencedTable}(${fk.referencedColumns.join(', ')}) ON DELETE ${fk.onDelete} ON UPDATE ${fk.onUpdate}`;
}
function describeUnique(constraint) {
    return constraint.columns.join(', ');
}
function describeCheck(constraint) {
    return constraint.expression;
}
function describeIndex(index) {
    const columns = index.columns.length > 0 ? index.columns.join(', ') : (index.definition ?? '');
    return index.isUnique ? `UNIQUE (${columns})` : columns;
}
/** Diffne dva pojmenované seznamy (sloupce/constrainty/indexy jedné tabulky). */
function diffNamed(oldItems, newItems, keyFn, describe, equal, objectType, pathPrefix, items) {
    const oldMap = byKey(oldItems, keyFn);
    const newMap = byKey(newItems, keyFn);
    const keys = new Set([...oldMap.keys(), ...newMap.keys()]);
    for (const key of keys) {
        const before = oldMap.get(key);
        const after = newMap.get(key);
        const path = `${pathPrefix}.${key}`;
        if (before && after) {
            if (!equal(before, after)) {
                items.push({
                    changeType: 'changed',
                    objectType,
                    path,
                    oldValue: describe(before),
                    newValue: describe(after),
                });
            }
        }
        else if (after) {
            items.push({ changeType: 'added', objectType, path, oldValue: null, newValue: describe(after) });
        }
        else if (before) {
            items.push({ changeType: 'removed', objectType, path, oldValue: describe(before), newValue: null });
        }
    }
}
function joinCols(columns) {
    return columns.join(' ');
}
/**
 * Epic 9 — najde skutečné páry přejmenování v pojmenovaném seznamu (tabulky
 * nebo sloupce, jediné dva typy s `renamedFrom`, viz `model.ts`). Vrací mapu
 * `staré jméno -> nové jméno`, jen pro páry, kde staré jméno v `newItems`
 * NEEXISTUJE jako samostatná položka (jinak by nešlo o rename, ale o
 * náhodnou shodu `renamedFrom` s jinak nesouvisející novou položkou).
 */
function findRenamePairs(oldItems, newItems) {
    const oldNames = new Set(oldItems.map((item) => item.name));
    const newNames = new Set(newItems.map((item) => item.name));
    const pairs = new Map();
    for (const item of newItems) {
        if (!item.renamedFrom)
            continue;
        if (!oldNames.has(item.renamedFrom))
            continue;
        if (newNames.has(item.renamedFrom))
            continue;
        pairs.set(item.renamedFrom, item.name);
    }
    return pairs;
}
function columnEqual(a, b) {
    return (a.rawType === b.rawType &&
        a.canonicalType === b.canonicalType &&
        a.nullable === b.nullable &&
        a.defaultValue === b.defaultValue &&
        a.comment === b.comment);
}
function primaryKeyEqual(a, b) {
    if (a === null || b === null)
        return a === b;
    return a.name === b.name && joinCols(a.columns) === joinCols(b.columns);
}
function foreignKeyEqual(a, b) {
    return (joinCols(a.columns) === joinCols(b.columns) &&
        a.referencedSchema === b.referencedSchema &&
        a.referencedTable === b.referencedTable &&
        joinCols(a.referencedColumns) === joinCols(b.referencedColumns) &&
        a.onDelete === b.onDelete &&
        a.onUpdate === b.onUpdate);
}
function uniqueEqual(a, b) {
    return joinCols(a.columns) === joinCols(b.columns);
}
function checkEqual(a, b) {
    return a.expression === b.expression;
}
function indexEqual(a, b) {
    return a.isUnique === b.isUnique && joinCols(a.columns) === joinCols(b.columns) && a.definition === b.definition;
}
/**
 * Epic 9 — jako `diffNamed`, ale pro sloupce navíc rozpozná přejmenování
 * (`detectRenames`). Pár `staré jméno -> nové jméno` (viz `findRenamePairs`)
 * dostane VLASTNÍ `renamed` položku (na cestě se starým jménem — proto ji
 * konflikt-detekce v Epic 9 dokáže spárovat s DB driftem beze změny) a
 * SAMOSTATNOU `changed` položku, pokud se u přejmenovaného sloupce zároveň
 * změnilo i něco jiného (typ, nullable, …) — dvě různé `changeType` na stejné
 * cestě nekolidují, `diffItemKey` (Epic 2, `sync.ts`) je součástí klíče.
 * Zbytek (skutečně nepřejmenované sloupce) doriešuje beze změny `diffNamed`.
 */
function diffColumns(before, after, prefix, detectRenames, items) {
    const renamePairs = detectRenames ? findRenamePairs(before, after) : new Map();
    for (const [oldName, newName] of renamePairs) {
        const beforeColumn = before.find((candidate) => candidate.name === oldName);
        const afterColumn = after.find((candidate) => candidate.name === newName);
        const path = `${prefix}.${oldName}`;
        items.push({ changeType: 'renamed', objectType: 'column', path, oldValue: oldName, newValue: newName });
        if (!columnEqual(beforeColumn, afterColumn)) {
            items.push({
                changeType: 'changed',
                objectType: 'column',
                path,
                oldValue: describeColumn(beforeColumn),
                newValue: describeColumn(afterColumn),
            });
        }
    }
    const renamedNewNames = new Set(renamePairs.values());
    const remainingBefore = before.filter((column) => !renamePairs.has(column.name));
    const remainingAfter = after.filter((column) => !renamedNewNames.has(column.name));
    diffNamed(remainingBefore, remainingAfter, (c) => c.name, describeColumn, columnEqual, 'column', prefix, items);
}
function diffTable(before, after, items, detectRenames = false) {
    const prefix = tableKey(before);
    if (before.comment !== after.comment) {
        items.push({
            changeType: 'changed',
            objectType: 'table',
            path: prefix,
            oldValue: before.comment,
            newValue: after.comment,
        });
    }
    diffColumns(before.columns, after.columns, prefix, detectRenames, items);
    if (!primaryKeyEqual(before.primaryKey, after.primaryKey)) {
        items.push({
            changeType: before.primaryKey && after.primaryKey ? 'changed' : after.primaryKey ? 'added' : 'removed',
            objectType: 'primaryKey',
            path: `${prefix}.PRIMARY KEY`,
            oldValue: before.primaryKey ? describePrimaryKey(before.primaryKey) : null,
            newValue: after.primaryKey ? describePrimaryKey(after.primaryKey) : null,
        });
    }
    diffNamed(before.foreignKeys, after.foreignKeys, (fk) => fk.name, describeForeignKey, foreignKeyEqual, 'foreignKey', prefix, items);
    diffNamed(before.uniqueConstraints, after.uniqueConstraints, (u) => u.name, describeUnique, uniqueEqual, 'uniqueConstraint', prefix, items);
    diffNamed(before.checkConstraints, after.checkConstraints, (c) => c.name, describeCheck, checkEqual, 'checkConstraint', prefix, items);
    diffNamed(before.indexes, after.indexes, (i) => i.name, describeIndex, indexEqual, 'index', prefix, items);
}
/**
 * Epic 9 — tabulková obdoba `findRenamePairs`, ale klíčovaná `schema.name`
 * (ne jen `name`) — `renamedFrom` na tabulce nese jen holé jméno (přejmenování
 * nikdy neposouvá tabulku mezi schématy), takže pár se hledá v rámci stejného
 * `table.schema`.
 */
function findTableRenamePairs(oldTables, newTables) {
    const oldKeys = new Set(oldTables.map(tableKey));
    const newKeys = new Set(newTables.map(tableKey));
    const pairs = new Map();
    for (const table of newTables) {
        if (!table.renamedFrom)
            continue;
        const oldKey = `${table.schema}.${table.renamedFrom}`;
        if (!oldKeys.has(oldKey))
            continue;
        if (newKeys.has(oldKey))
            continue;
        pairs.set(oldKey, tableKey(table));
    }
    return pairs;
}
/**
 * Porovná dva strukturální stavy modelu (Epic 3, E3-US6; Epic 2 `sync.ts`;
 * Epic 9 `changeScript.ts`) — name-based matching (viz hlavička souboru),
 * volitelně (Epic 9) i s detekcí přejmenování.
 */
export function compareSchemaModels(oldSchema, newSchema, options = {}) {
    const items = [];
    const oldTables = byKey(oldSchema.tables, tableKey);
    const newTables = byKey(newSchema.tables, tableKey);
    const detectRenames = options.detectRenames ?? false;
    // Tabulkové přejmenování se řeší JEDNOU, dopředu — `renamedFrom` je jen na
    // `newSchema` straně (viz `model.ts`), takže páry hledáme jen tam. Pár se
    // pak diffuje jako obyčejná "matched" dvojice (`diffTable`), aby se
    // zachytily i ostatní změny na přejmenované tabulce, plus samostatná
    // `renamed` položka na cestě se STARÝM klíčem (na tu se pak umí napojit
    // detekce konfliktů v Epic 9, `changeScript.ts`). Staré i nové klíče páru
    // se vyřadí z běžné add/remove smyčky níže, ať se nezdvojí.
    const renamePairs = detectRenames ? findTableRenamePairs(oldSchema.tables, newSchema.tables) : new Map();
    const consumedOldKeys = new Set(renamePairs.keys());
    const consumedNewKeys = new Set(renamePairs.values());
    for (const [oldKey, newKey] of renamePairs) {
        const before = oldTables.get(oldKey);
        const after = newTables.get(newKey);
        items.push({ changeType: 'renamed', objectType: 'table', path: oldKey, oldValue: before.name, newValue: after.name });
        diffTable(before, after, items, detectRenames);
    }
    const keys = new Set([...oldTables.keys(), ...newTables.keys()]);
    for (const key of keys) {
        if (consumedOldKeys.has(key) || consumedNewKeys.has(key))
            continue;
        const before = oldTables.get(key);
        const after = newTables.get(key);
        if (before && after) {
            diffTable(before, after, items, detectRenames);
        }
        else if (after) {
            items.push({ changeType: 'added', objectType: 'table', path: key, oldValue: null, newValue: describeTable(after) });
        }
        else if (before) {
            items.push({ changeType: 'removed', objectType: 'table', path: key, oldValue: describeTable(before), newValue: null });
        }
    }
    return items;
}
//# sourceMappingURL=diff.js.map